import React, {
  createContext,
  FC,
  ReactElement,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { ROLE_NAME } from 'enums';
import { reportError } from 'lib/reportError';
import {
  canAttemptSessionResync,
  resyncStaleSession,
  syncTokensWithAccount,
} from 'lib/sessionSync';
import {
  AccountApi,
  APP_DATA_MUTATED_EVENT,
  AUTH_SESSION_EXPIRED_EVENT,
  BillingApi,
  FORBIDDEN_ACCESS_EVENT,
  IBillingStatusResponse,
  IOrganizationFrozenResponse,
  ORGANIZATION_FROZEN_EVENT,
  refreshAccessToken,
} from 'services';
import {
  AUTH_SESSION_CHANGED_EVENT,
  getAuthSessionRevision,
  isAuthSessionCurrent,
  notifyAuthSessionChanged,
} from 'services/auth/session';
import { IOrganization, IUser } from 'types';

interface IUserContext {
  user: IUser | null;
  isGlobalLoading: boolean;
  setIsGlobalLoading: (globalIsLoading: boolean) => void;
  setUser: (user: IUser | null) => void;
  currentOrganization: IOrganization | null;
  setCurrentOrganization: (currentOrganization: IOrganization | null) => void;
  isAppInitialized: boolean;
  setIsAppInitialized: (isAppInitialized: boolean) => void;
  isShowCompleteProfileButton: boolean;
  setIsShowCompleteProfileButton: (isShowCompleteProfileButton: boolean) => void;
  billingStatus: IBillingStatusResponse | null;
  refreshBillingStatus: () => Promise<IBillingStatusResponse | null>;
  frozenOrganization: IOrganizationFrozenResponse | null;
  forbiddenAccess: { message: string } | null;
  clearForbiddenAccess: () => void;
}

const initialContext = {} as IUserContext;
const UserContext = createContext<IUserContext>(initialContext);

const UserProvider: FC<{ children: ReactNode }> = ({ children }): ReactElement => {
  const [user, setUser] = useState<IUser | null>(null);
  const [isGlobalLoading, setIsGlobalLoading] = useState(false);
  const [isShowCompleteProfileButton, setIsShowCompleteProfileButton] = useState(true);
  const [currentOrganization, setCurrentOrganization] = useState<IOrganization | null>(
    null,
  );
  const [isAppInitialized, setIsAppInitialized] = useState<boolean>(false);
  const [billingStatus, setBillingStatus] = useState<IBillingStatusResponse | null>(null);
  const [frozenOrganization, setFrozenOrganization] =
    useState<IOrganizationFrozenResponse | null>(null);
  const [forbiddenAccess, setForbiddenAccess] = useState<{ message: string } | null>(
    null,
  );

  const clearForbiddenAccess = useCallback((): void => {
    setForbiddenAccess(null);
  }, []);

  const isAdmin = user?.role.name === ROLE_NAME.ADMIN;
  const billingRequestId = useRef(0);
  const isMounted = useRef(true);
  useLayoutEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  useLayoutEffect(() => {
    billingRequestId.current += 1;
    setBillingStatus(null);
    setFrozenOrganization(null);
    setForbiddenAccess(null);
    notifyAuthSessionChanged();
  }, [user?.id, user?.role.name, currentOrganization?.id]);

  const refreshBillingStatus =
    useCallback(async (): Promise<IBillingStatusResponse | null> => {
      const requestId = billingRequestId.current + 1;
      billingRequestId.current = requestId;
      // Only admins are authorized to check billing status on the backend —
      // every other role gets a 403, which trips the global forbidden-access
      // handler and blanks out whatever page the user is actually on.
      if (!currentOrganization || !isAdmin) {
        setBillingStatus(null);
        setFrozenOrganization(null);
        return null;
      }

      try {
        const nextStatus = await new BillingApi().getStatus();
        if (billingRequestId.current !== requestId) return null;
        setBillingStatus(nextStatus);

        if (nextStatus.accessStatus !== 'FROZEN') {
          setFrozenOrganization(null);
        }

        return nextStatus;
      } catch {
        return null;
      }
    }, [currentOrganization, isAdmin]);

  const value = useMemo(
    () => ({
      user,
      setIsGlobalLoading,
      isGlobalLoading,
      setUser,
      currentOrganization,
      setCurrentOrganization,
      isAppInitialized,
      setIsAppInitialized,
      isShowCompleteProfileButton,
      setIsShowCompleteProfileButton,
      billingStatus,
      refreshBillingStatus,
      frozenOrganization,
      forbiddenAccess,
      clearForbiddenAccess,
    }),
    [
      user,
      isGlobalLoading,
      currentOrganization,
      isAppInitialized,
      isShowCompleteProfileButton,
      billingStatus,
      refreshBillingStatus,
      frozenOrganization,
      forbiddenAccess,
      clearForbiddenAccess,
    ],
  );

  useEffect(() => {
    const onSessionExpired = (): void => {
      setUser(null);
      setCurrentOrganization(null);
      setBillingStatus(null);
      setFrozenOrganization(null);
      setForbiddenAccess(null);
    };

    window.addEventListener(AUTH_SESSION_EXPIRED_EVENT, onSessionExpired);

    return () => {
      window.removeEventListener(AUTH_SESSION_EXPIRED_EVENT, onSessionExpired);
    };
  }, []);

  useEffect(() => {
    if (user && currentOrganization) {
      refreshBillingStatus();
    } else {
      setBillingStatus(null);
      setFrozenOrganization(null);
    }
  }, [user, currentOrganization?.id, refreshBillingStatus]);

  useEffect(() => {
    let active = true;
    const onAppDataMutated = (): void => {
      refreshBillingStatus();
    };
    const onOrganizationFrozen = (event: Event): void => {
      setFrozenOrganization((event as CustomEvent<IOrganizationFrozenResponse>).detail);
    };
    const onForbiddenAccess = (event: Event): void => {
      const owner = getAuthSessionRevision();
      const isCurrent = (): boolean =>
        active && isMounted.current && isAuthSessionCurrent(owner);
      const response = (event as CustomEvent<{ message?: string }>).detail;
      const showAccessDenied = (): void => {
        if (!isCurrent()) return;
        setForbiddenAccess({
          message: response?.message || 'You do not have access to this data.',
        });
      };

      if (!canAttemptSessionResync()) {
        showAccessDenied();
        return;
      }

      // A token issued before an approval or role change is the likeliest cause of a
      // surprise 403; repair it and reload once instead of blocking the page.
      resyncStaleSession()
        .then(repaired => {
          if (!isCurrent()) return;
          if (repaired) {
            window.location.reload();
          } else {
            showAccessDenied();
          }
        })
        .catch(showAccessDenied);
    };

    window.addEventListener(APP_DATA_MUTATED_EVENT, onAppDataMutated);
    window.addEventListener(ORGANIZATION_FROZEN_EVENT, onOrganizationFrozen);
    window.addEventListener(FORBIDDEN_ACCESS_EVENT, onForbiddenAccess);

    return () => {
      active = false;
      window.removeEventListener(APP_DATA_MUTATED_EVENT, onAppDataMutated);
      window.removeEventListener(ORGANIZATION_FROZEN_EVENT, onOrganizationFrozen);
      window.removeEventListener(FORBIDDEN_ACCESS_EVENT, onForbiddenAccess);
    };
  }, [currentOrganization?.id, refreshBillingStatus]);

  useEffect(() => {
    if (isAppInitialized) {
      return undefined;
    }

    let cancelled = false;
    const owner = getAuthSessionRevision();
    const isCurrent = (): boolean =>
      !cancelled && isMounted.current && isAuthSessionCurrent(owner);
    const discardInitialization = (): void => {
      cancelled = true;
      // A newer login/logout owns the account; startup must still finish.
      if (isMounted.current) setIsAppInitialized(true);
    };
    window.addEventListener(AUTH_SESSION_CHANGED_EVENT, discardInitialization);
    window.addEventListener(AUTH_SESSION_EXPIRED_EVENT, discardInitialization);

    (async () => {
      const accessToken = localStorage.getItem('accessToken');
      const refreshToken = localStorage.getItem('refreshToken');

      if (accessToken && refreshToken) {
        try {
          await refreshAccessToken();
          if (!isCurrent()) return;
          const currentUser = await new AccountApi().me();
          if (!isCurrent()) return;
          // The plain refresh can keep the role the token was first issued with, so a
          // person approved while signed out would keep getting 403s until re-login.
          await syncTokensWithAccount(currentUser).catch(error => {
            if (isCurrent()) reportError(error, 'UserProvider.syncTokensWithAccount');
          });

          if (isCurrent()) {
            setUser(currentUser);
            setCurrentOrganization(currentUser.organization);
            setIsAppInitialized(true);
          }
        } catch {
          // Failed initialization keeps the existing signed-out recovery flow.
        } finally {
          if (!cancelled && isMounted.current) setIsAppInitialized(true);
        }
      } else if (!cancelled) {
        setIsAppInitialized(true);
      }
    })();

    return () => {
      cancelled = true;
      window.removeEventListener(AUTH_SESSION_CHANGED_EVENT, discardInitialization);
      window.removeEventListener(AUTH_SESSION_EXPIRED_EVENT, discardInitialization);
    };
  }, [isAppInitialized]);

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
};

const useUserData = (): IUserContext => useContext(UserContext);

export { UserProvider };
export { useUserData };
